/* ==========================================================================
   PracticeAddressManager
   Shared multi-address editor for a provider's practice/office locations.
   Used by both the provider self-service profile page and the pharmacy-admin
   ProviderSettingsModal. Works with either the provider-side API
   (`@/api/auth`) or the admin-side API (`@/api/admin`) via the passed
   callbacks — the UI itself knows nothing about auth.

   Issues #140 / #180.
   ========================================================================== */

import { useEffect, useState, useCallback } from 'react'
import { Plus, Trash2, Pencil, Star, Loader2, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Checkbox } from '@/components/ui/Checkbox'
import { Combobox } from '@/components/ui/Combobox'
import { FormField } from '@/components/ui/FormField'
import { Alert } from '@/components/ui/Alert'
import {
  COUNTRIES,
  DEFAULT_COUNTRY,
  formatPostalCode,
  isUSStateCountry,
  postalLabel,
  postalPlaceholder,
  regionLabel,
  regionOptions,
  regionPlaceholder,
  validatePostalCode,
} from '@/utils/countries'
import { formatPracticeAddressLine } from '@/utils/address'
import { providerPayLabel } from '@/utils/paymentLabels'
import { usePortal } from '@/context/PortalContext'
import { PaymentType, DeliveryMethod } from '@/api/types'
import type {
  ProviderAddressCreate,
  ProviderAddressListResponse,
  ProviderAddressResponse,
  ProviderAddressUpdate,
} from '@/api/types'

// Every option, in the order the checkboxes render and the lists are stored.
const PAYMENT_TYPE_OPTIONS: PaymentType[] = [
  PaymentType.PATIENT_PAY,
  PaymentType.PROVIDER_PAY,
]

const DELIVERY_METHOD_OPTIONS: DeliveryMethod[] = [
  DeliveryMethod.PATIENT_PICKUP,
  DeliveryMethod.PATIENT_SHIP,
  DeliveryMethod.PROVIDER_PICKUP,
  DeliveryMethod.PROVIDER_SHIP,
]

/** Add or remove `value` from `current`, keeping `order`'s ordering. */
function toggleOption<T>(
  order: readonly T[],
  current: T[],
  value: T,
  checked: boolean
): T[] {
  const next = new Set(current)
  if (checked) next.add(value)
  else next.delete(value)
  return order.filter((option) => next.has(option))
}

export interface PracticeAddressManagerHandlers {
  list: () => Promise<ProviderAddressListResponse>
  create: (data: ProviderAddressCreate) => Promise<ProviderAddressResponse>
  update: (
    addressId: string,
    data: ProviderAddressUpdate
  ) => Promise<ProviderAddressResponse>
  remove: (addressId: string) => Promise<void>
}

export interface PracticeAddressManagerProps {
  handlers: PracticeAddressManagerHandlers
  /** When true, renders addresses as read-only with a lock message. */
  readOnly?: boolean
  /** Optional message shown above the list (e.g., lock explanation). */
  lockMessage?: string
  /** Called whenever the address list mutates so the parent can refresh. */
  onChange?: (addresses: ProviderAddressResponse[]) => void
  /**
   * When true, expose the pharmacy-configured payment/delivery option
   * checkboxes on the address editor (issue #192, PORTAL-84). Admin-only.
   */
  showDefaults?: boolean
  /**
   * When true, expose the country field and adapt the state input based on
   * the selected country (issue #190). Controlled by the pharmacy-level
   * ``allow_country_field`` setting.
   */
  showCountryField?: boolean
  /**
   * ISO-3166 alpha-2 country new addresses default to (PORTAL-161). A Canadian
   * pharmacy passes 'CA' so its providers get Province / Postal Code inputs
   * without having to expose the country combobox.
   */
  defaultCountry?: string
}

interface FormState {
  office_name: string
  street: string
  city: string
  state: string | null
  zip: string
  country: string
  is_primary: boolean
  default_payment_types: PaymentType[]
  default_delivery_methods: DeliveryMethod[]
}

function makeEmptyForm(country: string): FormState {
  return {
    office_name: '',
    street: '',
    city: '',
    state: null,
    zip: '',
    country,
    is_primary: false,
    default_payment_types: [],
    default_delivery_methods: [],
  }
}

const formatAddressLine = formatPracticeAddressLine

const paymentTypeLabel = (type: PaymentType, providerPay: string) =>
  type === PaymentType.PROVIDER_PAY ? providerPay : 'Patient Pay'

const DELIVERY_METHOD_LABEL: Record<DeliveryMethod, string> = {
  [DeliveryMethod.PATIENT_PICKUP]: 'Patient Pickup',
  [DeliveryMethod.PATIENT_SHIP]: 'Patient Ship',
  [DeliveryMethod.PROVIDER_PICKUP]: 'Provider Pickup',
  [DeliveryMethod.PROVIDER_SHIP]: 'Provider Ship',
}

/**
 * Validate the editor form. ``stored`` is the address being edited (if any):
 * a postal code the user did not touch is not re-validated, so rows saved
 * under earlier, looser rules stay editable (mirrors the backend, PORTAL-161).
 */
function validate(
  form: FormState,
  stored?: Pick<ProviderAddressResponse, 'zip' | 'country'>
): Record<string, string> {
  const errors: Record<string, string> = {}
  const zipUnchanged =
    stored !== undefined &&
    form.zip.trim() === stored.zip &&
    form.country === (stored.country || DEFAULT_COUNTRY)
  if (!form.street.trim()) errors.street = 'Street is required'
  if (!form.city.trim()) errors.city = 'City is required'
  const stateValue = form.state?.trim() ?? ''
  const region = regionLabel(form.country)
  if (!stateValue) {
    errors.state = `${region} is required`
  } else if (
    isUSStateCountry(form.country) &&
    !/^[A-Za-z]{2}$/.test(stateValue)
  ) {
    errors.state = 'State must be a 2-letter code'
  }
  const postal = postalLabel(form.country, { short: true })
  if (!form.zip.trim()) {
    errors.zip = `${postal} is required`
  } else if (!zipUnchanged) {
    const postalError = validatePostalCode(form.country, form.zip)
    if (postalError) errors.zip = postalError
  }
  return errors
}

export function PracticeAddressManager({
  handlers,
  readOnly = false,
  lockMessage,
  onChange,
  showDefaults = false,
  showCountryField = false,
  defaultCountry = DEFAULT_COUNTRY,
}: PracticeAddressManagerProps) {
  const { config } = usePortal()
  const providerPay = providerPayLabel(config?.use_clinic_pay_label)
  const emptyForm = makeEmptyForm(defaultCountry)
  const [addresses, setAddresses] = useState<ProviderAddressResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const [isFormOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await handlers.list()
      setAddresses(data.addresses)
      onChange?.(data.addresses)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load addresses')
    } finally {
      setLoading(false)
    }
  }, [handlers, onChange])

  useEffect(() => {
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const openCreate = () => {
    setEditingId(null)
    setForm({ ...emptyForm, is_primary: addresses.length === 0 })
    setFormErrors({})
    setFormOpen(true)
  }

  const openEdit = (address: ProviderAddressResponse) => {
    setEditingId(address.id)
    setForm({
      office_name: address.office_name ?? '',
      street: address.street,
      city: address.city,
      state: address.state,
      zip: address.zip,
      country: address.country || DEFAULT_COUNTRY,
      is_primary: address.is_primary,
      default_payment_types: address.default_payment_types ?? [],
      default_delivery_methods: address.default_delivery_methods ?? [],
    })
    setFormErrors({})
    setFormOpen(true)
  }

  const cancel = () => {
    setFormOpen(false)
    setEditingId(null)
    setForm(emptyForm)
    setFormErrors({})
  }

  const submit = async () => {
    const stored = editingId ? addresses.find((a) => a.id === editingId) : undefined
    const errors = validate(form, stored)
    setFormErrors(errors)
    if (Object.keys(errors).length > 0) return

    setSubmitting(true)
    try {
      const payload: ProviderAddressCreate = {
        office_name: form.office_name.trim() || null,
        street: form.street.trim(),
        city: form.city.trim(),
        state: (form.state as string).trim(),
        zip: form.zip.trim(),
        country: form.country || DEFAULT_COUNTRY,
        is_primary: form.is_primary,
      }
      if (showDefaults) {
        payload.default_payment_types = form.default_payment_types
        payload.default_delivery_methods = form.default_delivery_methods
      }
      if (editingId) {
        await handlers.update(editingId, payload)
      } else {
        await handlers.create(payload)
      }
      cancel()
      await refresh()
    } catch (err) {
      setFormErrors({
        _form: err instanceof Error ? err.message : 'Failed to save address',
      })
    } finally {
      setSubmitting(false)
    }
  }

  // Allow Enter in text inputs to submit — but scoped to the editor div so
  // we don't accidentally submit an outer page form that may wrap us.
  const onEditorKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT') {
        e.preventDefault()
        void submit()
      }
    }
  }

  const handleDelete = async (address: ProviderAddressResponse) => {
    if (
      !window.confirm(
        `Delete the practice address "${address.office_name || address.street}"?`
      )
    ) {
      return
    }
    setBusyId(address.id)
    try {
      await handlers.remove(address.id)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete')
    } finally {
      setBusyId(null)
    }
  }

  const setPrimary = async (address: ProviderAddressResponse) => {
    if (address.is_primary) return
    setBusyId(address.id)
    try {
      await handlers.update(address.id, { is_primary: true })
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      {lockMessage && (
        <Alert variant="info">{lockMessage}</Alert>
      )}
      {error && <Alert variant="error">{error}</Alert>}

      {loading ? (
        <div className="flex items-center justify-center py-6 text-[var(--color-gray-500)]">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
        </div>
      ) : addresses.length === 0 ? (
        <div className="text-sm text-[var(--color-gray-500)] italic">
          No practice addresses on file yet.
        </div>
      ) : (
        <ul className="space-y-3">
          {addresses.map((address) => (
            <li key={address.id}>
              <Card className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[var(--color-gray-500)] flex-shrink-0" />
                      <span className="font-medium text-[var(--color-charcoal)] truncate">
                        {address.office_name || 'Practice Address'}
                      </span>
                      {address.is_primary && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--color-success)] bg-[var(--color-success-light)] px-2 py-0.5 rounded-full">
                          <Star className="h-3 w-3" /> Primary
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-[var(--color-gray-600)] break-words">
                      {formatAddressLine(address)}
                    </p>
                    {showDefaults &&
                      (address.default_payment_types.length > 0 ||
                        address.default_delivery_methods.length > 0) && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {address.default_payment_types.length > 0 && (
                            <span className="inline-flex items-center text-xs font-medium text-[var(--color-charcoal)] bg-[var(--color-gray-100)] px-2 py-0.5 rounded-full">
                              Payment:{' '}
                              {address.default_payment_types
                                .map((type) => paymentTypeLabel(type, providerPay))
                                .join(', ')}
                            </span>
                          )}
                          {address.default_delivery_methods.length > 0 && (
                            <span className="inline-flex items-center text-xs font-medium text-[var(--color-charcoal)] bg-[var(--color-gray-100)] px-2 py-0.5 rounded-full">
                              Delivery:{' '}
                              {address.default_delivery_methods
                                .map((method) => DELIVERY_METHOD_LABEL[method])
                                .join(', ')}
                            </span>
                          )}
                        </div>
                      )}
                  </div>
                  {!readOnly && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!address.is_primary && (
                        <button
                          type="button"
                          onClick={() => setPrimary(address)}
                          disabled={busyId === address.id}
                          className="p-2 text-[var(--color-gray-500)] hover:text-[var(--color-charcoal)] disabled:opacity-50"
                          title="Make primary"
                          aria-label="Make primary"
                        >
                          <Star className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openEdit(address)}
                        className="p-2 text-[var(--color-gray-500)] hover:text-[var(--color-charcoal)]"
                        title="Edit address"
                        aria-label="Edit address"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(address)}
                        disabled={busyId === address.id || addresses.length <= 1}
                        className="p-2 text-[var(--color-gray-500)] hover:text-[var(--color-error)] disabled:opacity-50"
                        title={
                          addresses.length <= 1
                            ? 'At least one address is required'
                            : 'Delete address'
                        }
                        aria-label="Delete address"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {!readOnly && !isFormOpen && (
        <div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={openCreate}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add practice address
          </Button>
        </div>
      )}

      {!readOnly && isFormOpen && (
        <Card className="p-4 border-[var(--pharmacy-primary)]">
          {/* Rendered as a <div>, not a <form>, because this component is
              often mounted inside another page-level <form> (e.g., the
              provider profile page). Nested forms are invalid HTML and
              browsers hoist inner submits to the outer form, which would
              wipe unsaved state. */}
          <div
            className="space-y-4"
            onKeyDown={onEditorKeyDown}
            role="group"
            aria-label={editingId ? 'Edit practice address' : 'Add practice address'}
          >
            <h4 className="text-sm font-semibold text-charcoal">
              {editingId ? 'Edit practice address' : 'Add practice address'}
            </h4>

            {formErrors._form && (
              <Alert variant="error">{formErrors._form}</Alert>
            )}

            <FormField label="Office / Clinic Name" htmlFor="office_name">
              <Input
                id="office_name"
                value={form.office_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, office_name: e.target.value }))
                }
                placeholder="e.g., Downtown Clinic (optional)"
                disabled={submitting}
              />
            </FormField>

            <FormField
              label="Street Address"
              required
              error={formErrors.street}
              htmlFor="street"
            >
              <Input
                id="street"
                value={form.street}
                onChange={(e) =>
                  setForm((f) => ({ ...f, street: e.target.value }))
                }
                disabled={submitting}
                error={!!formErrors.street}
              />
            </FormField>

            {showCountryField && (
              <FormField label="Country" required htmlFor="country">
                <Combobox
                  options={COUNTRIES}
                  value={form.country}
                  onChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      country: v || defaultCountry,
                      // Clear the region and postal code when the country
                      // changes — their option lists and formats differ.
                      state:
                        regionOptions(v) !== regionOptions(f.country)
                          ? null
                          : f.state,
                      zip: (v || defaultCountry) === f.country ? f.zip : '',
                    }))
                  }
                  placeholder="Country"
                  disabled={submitting}
                  clearable={false}
                />
              </FormField>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FormField
                label="City"
                required
                error={formErrors.city}
                htmlFor="city"
              >
                <Input
                  id="city"
                  value={form.city}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, city: e.target.value }))
                  }
                  disabled={submitting}
                  error={!!formErrors.city}
                />
              </FormField>
              <FormField
                label={regionLabel(form.country)}
                required
                error={formErrors.state}
                htmlFor="state"
              >
                {regionOptions(form.country) ? (
                  <Select
                    options={regionOptions(form.country) ?? []}
                    value={form.state}
                    onChange={(v) => setForm((f) => ({ ...f, state: v }))}
                    placeholder={regionLabel(form.country)}
                    disabled={submitting}
                    error={!!formErrors.state}
                  />
                ) : (
                  <Input
                    id="state"
                    value={form.state ?? ''}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, state: e.target.value }))
                    }
                    placeholder={regionPlaceholder(form.country)}
                    disabled={submitting}
                    error={!!formErrors.state}
                    maxLength={100}
                  />
                )}
              </FormField>
              <FormField
                label={postalLabel(form.country, { short: true })}
                required
                error={formErrors.zip}
                htmlFor="zip"
              >
                <Input
                  id="zip"
                  inputMode={isUSStateCountry(form.country) ? 'numeric' : 'text'}
                  placeholder={postalPlaceholder(form.country)}
                  value={form.zip}
                  onChange={(e) => {
                    const next = formatPostalCode(form.country, e.target.value)
                    setForm((f) => ({ ...f, zip: next }))
                  }}
                  disabled={submitting}
                  error={!!formErrors.zip}
                  maxLength={10}
                />
              </FormField>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_primary}
                onChange={(e) =>
                  setForm((f) => ({ ...f, is_primary: e.target.checked }))
                }
                disabled={submitting || (addresses.length === 0 && !editingId)}
                className="h-4 w-4 rounded border-gray-300 text-[var(--pharmacy-primary)] focus:ring-[var(--pharmacy-secondary)]"
              />
              <span className="text-[var(--color-charcoal)]">
                Use as primary practice address
              </span>
            </label>

            {showDefaults && (
              <div className="space-y-3 border-t pt-4">
                <div>
                  <h5 className="text-sm font-semibold text-charcoal">
                    Prescription Defaults
                  </h5>
                  <p className="text-xs text-[var(--color-gray-600)] mt-1">
                    Leave every option unchecked to let the provider choose.
                    Check one option to lock this address to it. Check several
                    to limit the provider to those options while prescribing
                    from this address.
                  </p>
                </div>
                <fieldset>
                  <legend className="block text-sm font-medium text-[var(--color-charcoal)] mb-2">
                    Payment Type
                  </legend>
                  <div className="flex flex-wrap gap-4">
                    {PAYMENT_TYPE_OPTIONS.map((type) => (
                      <Checkbox
                        key={type}
                        label={paymentTypeLabel(type, providerPay)}
                        checked={form.default_payment_types.includes(type)}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            default_payment_types: toggleOption(
                              PAYMENT_TYPE_OPTIONS,
                              f.default_payment_types,
                              type,
                              e.target.checked
                            ),
                          }))
                        }
                        disabled={submitting}
                      />
                    ))}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="block text-sm font-medium text-[var(--color-charcoal)] mb-2">
                    Delivery Method
                  </legend>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    {DELIVERY_METHOD_OPTIONS.map((method) => (
                      <Checkbox
                        key={method}
                        label={DELIVERY_METHOD_LABEL[method]}
                        checked={form.default_delivery_methods.includes(method)}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            default_delivery_methods: toggleOption(
                              DELIVERY_METHOD_OPTIONS,
                              f.default_delivery_methods,
                              method,
                              e.target.checked
                            ),
                          }))
                        }
                        disabled={submitting}
                      />
                    ))}
                  </div>
                </fieldset>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={cancel}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                loading={submitting}
                disabled={submitting}
                onClick={() => void submit()}
              >
                {editingId ? 'Save changes' : 'Add address'}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}

export default PracticeAddressManager
